if (!customElements.get('blog-search')) {
  customElements.define(
    'blog-search',
    class BlogSearch extends HTMLElement {
      constructor() {
        super();
        this.flkty = null;
        this.flickityAttempts = 0;
        this.debounceTimer = null;
        this.lastQuery = '';
        this.abortController = null;
      }

      connectedCallback() {
        this.blogHandle = (this.dataset.blogHandle || '').trim();
        this.limit = parseInt(this.dataset.limit, 10) || 12;
        this.predictiveSearchUrl = this.dataset.predictiveSearchUrl || '/search/suggest';
        this.form = this.querySelector('[data-blog-search-form]');
        this.input = this.querySelector('[data-blog-search-input]');
        this.clearButton = this.querySelector('[data-blog-search-clear]');
        this.carousel = this.querySelector('[data-blog-carousel]');
        this.results = this.querySelector('[data-blog-results]');
        this.resultsGrid = this.querySelector('[data-blog-results-grid]');
        this.resultsEmpty = this.querySelector('[data-blog-results-empty]');

        this.initFlickity();
        this.syncClearState();

        if (this.input) {
          this.input.addEventListener('input', (event) => this.onInput(event));
          this.input.addEventListener('focus', () => this.onFocus());
          this.input.addEventListener('blur', () => this.onBlur());
        }

        if (this.clearButton) {
          this.clearButton.addEventListener('click', () => this.clearSearch());
        }

        if (this.form) {
          this.form.addEventListener('submit', (event) => this.onSubmit(event));
        }
      }

      initFlickity() {
        if (!this.carousel || this.flkty) return;

        if (typeof window.Flickity === 'undefined') {
          if (this.flickityAttempts < 20) {
            this.flickityAttempts += 1;
            window.setTimeout(() => this.initFlickity(), 150);
          }
          return;
        }

        this.flkty = new window.Flickity(this.carousel, {
          cellAlign: 'center',
          pageDots: false,
          prevNextButtons: false,
          contain: true,
          groupCells: true,
          rightToLeft: false,
          accessibility: false,
        });
      }

      isMobile() {
        return window.matchMedia('(max-width: 900px)').matches;
      }

      onFocus() {
        if (this.isMobile()) this.setAttribute('data-search-expanded', '');
      }

      onBlur() {
        if (!this.input || !this.input.value.trim()) {
          this.removeAttribute('data-search-expanded');
        }
      }

      syncClearState() {
        const hasQuery = Boolean(this.input && this.input.value.trim());
        this.toggleAttribute('data-has-query', hasQuery);
      }

      clearSearch() {
        if (!this.input) return;

        this.input.value = '';
        this.lastQuery = '';
        if (this.abortController) this.abortController.abort();
        this.setLoading(false);
        this.syncClearState();
        this.removeAttribute('data-search-expanded');
        this.showCarousel();
        this.input.focus();
      }

      onInput(event) {
        const query = (event.target.value || '').trim();
        window.clearTimeout(this.debounceTimer);
        this.syncClearState();

        if (!query) {
          this.lastQuery = '';
          if (this.abortController) this.abortController.abort();
          this.setLoading(false);
          this.showCarousel();
          return;
        }

        this.debounceTimer = window.setTimeout(() => this.search(query), 300);
      }

      onSubmit(event) {
        const query = this.input ? this.input.value.trim() : '';
        if (!query) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.search(query, true);
      }

      search(query, force) {
        if (!force && query === this.lastQuery) return;
        this.lastQuery = query;

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'article',
          'resources[limit]': String(this.limit),
          'resources[options][unavailable_products]': 'last',
        });

        this.setLoading(true);
        fetch(`${this.predictiveSearchUrl}.json?${params.toString()}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Search returned ${response.status}`);
            return response.json();
          })
          .then((data) => {
            if (query !== this.lastQuery) return;

            let articles =
              data &&
              data.resources &&
              data.resources.results &&
              data.resources.results.articles
                ? data.resources.results.articles
                : [];

            if (this.blogHandle) {
              const blogPrefix = `/blogs/${this.blogHandle}/`;
              articles = articles.filter(
                (article) => article.url && article.url.indexOf(blogPrefix) === 0
              );
            }

            this.renderResults(articles);
            this.setLoading(false);
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error('[blog-search] Search failed', error);
            this.setLoading(false);
          });
      }

      setLoading(isLoading) {
        this.toggleAttribute('data-loading', isLoading);
      }

      renderResults(articles) {
        this.showResults();

        if (!articles.length) {
          this.resultsGrid.innerHTML = '';
          this.resultsEmpty.hidden = false;
          return;
        }

        this.resultsEmpty.hidden = true;
        this.resultsGrid.innerHTML = articles.map((article) => this.cardMarkup(article)).join('');
      }

      cardMarkup(article) {
        const url = this.escape(article.url || '#');
        const title = this.escape(article.title || '');
        const imageSource =
          typeof article.image === 'string'
            ? article.image
            : article.image && article.image.url
              ? article.image.url
              : '';
        const image = imageSource
          ? `<img loading="lazy" src="${this.escape(imageSource)}" alt="${title}">`
          : '';
        const tags = Array.isArray(article.tags) ? article.tags.slice(0, 3).join(' | ') : '';
        const excerptSource =
          article.summary_html || article.summary || article.body || article.content || '';
        const excerptText = this.stripHtml(excerptSource);
        const words = excerptText ? excerptText.split(/\s+/).filter(Boolean).length : 0;
        const readingTime = `${Math.max(1, Math.round(words / 200))} Min Read`;
        const excerpt = this.escape(this.truncate(excerptText, 30));

        return `
          <a class="app-article-card" href="${url}">
            <div class="aa-img">${image}</div>
            <div class="bs-text text-black">
              <div class="bst-first">${this.escape(tags)} ${readingTime}</div>
              <h3 class="bst-title">${title}</h3>
              <div class="bst-sub">${excerpt}</div>
            </div>
          </a>
        `;
      }

      showCarousel() {
        if (this.results) this.results.hidden = true;
        if (this.resultsGrid) this.resultsGrid.innerHTML = '';
        if (this.resultsEmpty) this.resultsEmpty.hidden = true;
        if (this.carousel) this.carousel.hidden = false;

        if (this.flkty) {
          window.requestAnimationFrame(() => this.flkty.resize());
        } else {
          this.initFlickity();
        }
      }

      showResults() {
        if (this.carousel) this.carousel.hidden = true;
        if (this.results) this.results.hidden = false;
      }

      stripHtml(html) {
        const element = document.createElement('div');
        element.innerHTML = html || '';
        return (element.textContent || '').replace(/\s+/g, ' ').trim();
      }

      truncate(text, wordLimit) {
        const words = text.split(/\s+/);
        return words.length > wordLimit ? `${words.slice(0, wordLimit).join(' ')}…` : text;
      }

      escape(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}